import { useState, useEffect } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import WhatsAppButton from "../components/WhatsAppButton";
import { toast } from "../hooks/use-toast";
import api from "../services/api";
import { useTranslation } from "react-i18next";

const Contact = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    const load = async () => {
      const res = await api.get("/contact-settings");
      setSettings(res.data);
    };
    load();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const [errors, setErrors] = useState({});

  const validate = () => {
    let validationErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      validationErrors.name = "Name is required";
    } else if (formData.name.length < 3) {
      validationErrors.name = "Name must be at least 3 characters";
    }

    // Email validation
    if (!formData.email) {
      validationErrors.email = "Email is required";
    } else if (!formData.email.includes("@")) {
      validationErrors.email = "Please include '@' in the email address";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      validationErrors.email = "Email is invalid";
    }

    if (!formData.subject.trim()) {
      validationErrors.subject = "Subject is required";
    }

    // Message validation
    if (!formData.message.trim()) {
      validationErrors.message = "Message is required";
    } else if (formData.message.length < 10) {
      validationErrors.message = "Message must be at least 10 characters";
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      try {
        setLoading(true);
        // send massege to backend to send it to admin email
        await api.post("/contacts", formData);

        // UI only - show success toast
        toast({ title: t("contact.successMessage") });

        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
        setErrors({});
      } catch (err) {
        toast({
          title: t("contact.errorTitle"),
          description:
            err.response?.data?.message || t("contact.errorDescription"),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  if (!settings) return null;

  const contactInfo = [
    {
      icon: MapPin,
      title: t("contact.visit"),
      content: settings[`address_${lang}`],
      link: settings.map_link,
    },
    {
      icon: Phone,
      title: t("contact.call"),
      content: settings[`phone_1_${lang}`],
      link: `tel:${settings[`phone_1_${lang}`]}`,
    },
    {
      icon: Phone,
      title: t("contact.call"),
      content: settings[`phone_2_${lang}`],
      link: settings[`phone_2_${lang}`]
        ? `tel:${settings[`phone_2_${lang}`]}`
        : null,
    },
    {
      icon: Mail,
      title: t("contact.email"),
      content: settings.email,
      link: `mailto:${settings.email}`,
    },
    {
      icon: Clock,
      title: t("contact.hours"),
      content: settings[`business_hours_${lang}`],
    },
  ];

  return (
    <div>
      {/* Header */}
      <section className="bg-primary text-primary-foreground section-padding">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">
            {t("contact.title")}
          </h1>
          <p className="text-primary-foreground/80 max-w-2xl mx-auto">
            {t("contact.subtitle")}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div className="space-y-10">
              <div>
                <h2 className="text-3xl font-serif font-bold mb-6">
                  {t("contact.getInTouch")}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  {t("contact.infoText")}
                </p>
                <WhatsAppButton variant="full" />
              </div>

              {/* Contact Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="p-6 bg-card rounded-2xl shadow-soft"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                      <info.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-2">{info.title}</h3>
                    {info.link ? (
                      <a
                        href={info.link}
                        onClick={(e) => {
                          // For mailto links on desktop without mail client
                          if (
                            info.link.startsWith("mailto:") &&
                            !navigator.userAgent.includes("Mobile")
                          ) {
                            e.preventDefault();
                            window.open(
                              `https://mail.google.com/mail/?view=cm&fs=1&to=${settings.email}`,
                              "_blank",
                            );
                          }
                        }}
                        className="text-muted-foreground text-sm hover:text-primary"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        {info.content}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-card p-8 md:p-10 rounded-3xl shadow-soft">
              <h3 className="text-2xl font-serif font-bold mb-6">
                {t("contact.sendMessage")}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium mb-2"
                  >
                    {t("contact.name")}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    placeholder="John Doe"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium mb-2"
                  >
                    {t("contact.emaill")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    placeholder="john@example.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium mb-2"
                  >
                    {t("contact.subject")}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    placeholder="Product Inquiry"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium mb-2"
                  >
                    {t("contact.message")}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none"
                    placeholder="Tell us how we can help..."
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-hero w-full flex items-center justify-center gap-2"
                >
                  <Send size={18} />
                  <span>
                    {loading ? t("contact.sending") : t("contact.send")}
                  </span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
